import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { getDashboard } from '../services/dashboardApi';
import Card from '../components/Card';
import KpiCard from '../components/KpiCard';
import TotalOrdersCards from '../components/TotalOrdersCards';
import { useNavigate } from 'react-router-dom';
import CustomerStatusCards from '../components/CustomerStatusCards';
import GlobalSearchBar from '../components/GlobalSearchBar';
import DashboardInsights, { DashboardSkeleton } from '../components/DashboardInsights';
import ThemeToggle from '../components/ThemeToggle';
import { formatDate, formatDateTime } from '../utils/dateFormat';

/** One alert row — a small solid severity dot (not just a left-border
 * strip) reads faster at a glance than a border alone, and still uses the
 * tone color meaningfully rather than as decoration. */
function NotificationList({ title, items, tone, emptyText, renderItem }) {
  return (
    <Card title={title}>
      {items.length === 0 ? (
        <p style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)', margin: 0 }}>
          {emptyText}
        </p>
      ) : (
        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {items.map((item, i) => (
            <li
              key={i}
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 10,
                padding: '10px 2px',
                borderBottom: i < items.length - 1 ? '1px solid var(--color-border)' : 'none',
                fontSize: 'var(--font-size-sm)',
              }}
            >
              <span
                aria-hidden="true"
                style={{
                  width: 7,
                  height: 7,
                  borderRadius: '50%',
                  marginTop: 6,
                  flexShrink: 0,
                  background: `var(--color-${tone})`,
                }}
              />
              <span style={{ flex: 1 }}>{renderItem(item)}</span>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function StatusPill({ status }) {
  const styles = {
    completed: { bg: 'var(--color-accent-soft)', color: 'var(--color-accent)' },
    processing: { bg: '#FFF4E0', color: 'var(--color-warning)' },
    failed: { bg: 'var(--color-accent-warm-soft)', color: 'var(--color-danger)' },
    rolled_back: { bg: 'var(--color-surface-alt)', color: 'var(--color-text-muted)' },
  };
  const s = styles[status] || styles.rolled_back;
  return (
    <span
      style={{
        display: 'inline-block',
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: '0.02em',
        textTransform: 'capitalize',
        padding: '3px 9px',
        borderRadius: 999,
        background: s.bg,
        color: s.color,
      }}
    >
      {status.replace('_', ' ')}
    </span>
  );
}

/** "Good morning/afternoon/evening" based on the local clock — a more
 * natural greeting than a static "Welcome", and it replaces the need for a
 * separate tracked-uppercase date label sitting above the heading purely
 * as decoration; the date itself still appears, folded into the subline
 * below in normal sentence case. */
function getGreeting() {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
}

export default function DashboardPage() {
  const { user } = useAuth();
  const restricted = Boolean(user && user.refPartyId);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  // "Dispatched This Month" opens the Customer-wise Dispatch breakdown of the current month.
  const openThisMonthDispatch = () => {
    const now = new Date();
    navigate('/dispatch-analysis', { state: { autoOpen: { year: now.getFullYear(), month: now.getMonth() + 1, dimension: 'customer' } } });
  };
  const today = `${new Date().toLocaleDateString('en-IN', { weekday: 'long' })}, ${formatDate(new Date())}`;

  useEffect(() => {
    getDashboard()
      .then(setData)
      .catch((err) => setError(err.message || 'Could not load dashboard data.'));
  }, []);

  const attentionCount = data
    ? data.notifications.negativeStock.length + data.notifications.lowStock.length
    : 0;

  return (
    <div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-end',
          gap: 'var(--space-4)',
          flexWrap: 'wrap',
          marginBottom: 'var(--space-6)',
          paddingBottom: 'var(--space-4)',
          borderBottom: '1px solid var(--color-border)',
        }}
      >
        <div>
          <h1 style={{ fontFamily: 'var(--font-display)', marginBottom: 4 }}>
            {getGreeting()}{user ? `, ${user.fullName.split(' ')[0]}` : ''}
          </h1>
          <p style={{ color: 'var(--color-text-secondary)', margin: 0 }}>
            {today} — here&apos;s where operations stand right now.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', flex: '1 1 300px', justifyContent: 'flex-end' }}><GlobalSearchBar customersOnly={restricted} /><ThemeToggle /></div>
      </div>

      {error && <p style={{ color: 'var(--color-danger)' }}>{error}</p>}

      {!data && !error && <DashboardSkeleton />}

      {data && (
        <>
          <DashboardInsights restricted={restricted} onOpenMonthDispatch={openThisMonthDispatch} />
          {/* One emphasized "pulse of the month" metric alongside the
              rest, kept quieter — a deliberate point of hierarchy rather
              than five equally-weighted numbers in a row. */}
          <div
            style={{
              display: 'flex',
              gap: 'var(--space-4)',
              flexWrap: 'wrap',
              alignItems: 'stretch',
              marginBottom: 'var(--space-6)',
            }}
          >
            <div style={{ display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap', flex: '2 1 420px' }}>
              {!restricted && <KpiCard label="Total Designs" value={data.kpis.totalDesigns} />}
              {!restricted && (
                <KpiCard label="Total Stock Qty" value={data.kpis.totalStockQty.toLocaleString()} />
              )}
              <TotalOrdersCards total={data.kpis.totalOrders} />
              <CustomerStatusCards />
              {!restricted && (
                <KpiCard
                  label="Negative Stock Items"
                  value={data.kpis.negativeStockCount}
                  tone={data.kpis.negativeStockCount > 0 ? 'danger' : 'success'}
                />
              )}
            </div>
          </div>

          {!restricted && (
          <>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 'var(--space-3)' }}>
            <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 'var(--font-size-lg)', margin: 0 }}>
              Needs attention
            </h2>
            {attentionCount > 0 && (
              <span
                style={{
                  fontSize: 'var(--font-size-xs)',
                  fontWeight: 700,
                  color: 'var(--color-accent-warm)',
                  background: 'var(--color-accent-warm-soft)',
                  padding: '2px 9px',
                  borderRadius: 999,
                }}
              >
                {attentionCount} item{attentionCount === 1 ? '' : 's'}
              </span>
            )}
          </div>

          <div
            className="responsive-grid-2col"
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 'var(--space-5)',
              marginBottom: 'var(--space-6)',
            }}
          >
            <NotificationList
              title="Negative Stock Alerts"
              items={data.notifications.negativeStock}
              tone="danger"
              emptyText="No negative stock right now."
              renderItem={(item) => (
                <span>
                  <strong>{item.design_name}</strong> at {item.location_name}:{' '}
                  <span className="data-figure">{item.current_qty}</span>
                </span>
              )}
            />
            <NotificationList
              title="Low Stock Alerts (next 20 days)"
              items={data.notifications.lowStock}
              tone="warning"
              emptyText="No design is expected to run short in the next 20 days."
              renderItem={(item) => (
                <span>
                  <strong>{item.design_name}</strong> ({item.size_name}): stock{' '}
                  <span className="data-figure">{item.stock_qty}</span>, expected need{' '}
                  <span className="data-figure">{item.need_qty}</span> in 20 days ({item.customers}{' '}
                  customer{item.customers === 1 ? '' : 's'}) &mdash; short by{' '}
                  <strong className="data-figure">{item.shortage_qty}</strong>
                </span>
              )}
            />
          </div>

          <NotificationList
            title="AI Notifications"
            items={data.notifications.aiAlerts}
            tone="info"
            emptyText="No AI alerts right now."
            renderItem={(item) => {
              const m = /^(\w+) \((\w+)\): ([\s\S]*)$/.exec(item.message || '');
              if (!m) return <span>{item.message}</span>;
              const col = { critical: 'danger', high: 'warning', medium: 'info', low: 'success' }[m[2]] || 'info';
              return (
                <span>
                  <span style={{ display: 'inline-block', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', padding: '1px 7px', borderRadius: 999, marginRight: 8, color: `var(--color-${col})`, border: `1px solid var(--color-${col})` }}>{m[2]}</span>
                  <strong>{m[1]}:</strong> {m[3]}
                </span>
              );
            }}
          />
          <div style={{ textAlign: 'right', marginTop: 8 }}>
            <button type="button" onClick={() => navigate('/recommendations')} style={{ background: 'none', border: 'none', color: 'var(--color-accent)', cursor: 'pointer', font: 'inherit', fontSize: 'var(--font-size-sm)', fontWeight: 600 }}>View all recommendations →</button>
          </div>

          <Card title="Recent Import History" style={{ marginTop: 'var(--space-6)' }}>
            {data.recentImports.length === 0 ? (
              <p style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)', margin: 0 }}>
                No imports yet — upload an Order, Stock, Dispatch, or Production Excel to get
                started.
              </p>
            ) : (
              <div className="table-scroll">
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ textAlign: 'left', borderBottom: '1px solid var(--color-border)' }}>
                    <th style={{ padding: 'var(--space-2)', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>File</th>
                    <th style={{ padding: 'var(--space-2)', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>Type</th>
                    <th style={{ padding: 'var(--space-2)', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>Rows</th>
                    <th style={{ padding: 'var(--space-2)', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>Status</th>
                    <th style={{ padding: 'var(--space-2)', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>By</th>
                    <th style={{ padding: 'var(--space-2)', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>Uploaded On</th>
                  </tr>
                </thead>
                <tbody>
                  {data.recentImports.map((imp) => (
                    <tr key={imp.import_id} style={{ borderBottom: '1px solid var(--color-border)' }}>
                      <td style={{ padding: 'var(--space-2)', fontSize: 'var(--font-size-sm)' }}>{imp.file_name}</td>
                      <td style={{ padding: 'var(--space-2)', fontSize: 'var(--font-size-sm)', textTransform: 'capitalize' }}>
                        {imp.import_type}
                      </td>
                      <td className="data-figure" style={{ padding: 'var(--space-2)', fontSize: 'var(--font-size-sm)' }}>
                        {imp.accepted_rows}/{imp.total_rows}
                      </td>
                      <td style={{ padding: 'var(--space-2)', fontSize: 'var(--font-size-sm)' }}><StatusPill status={imp.status} /></td>
                      <td style={{ padding: 'var(--space-2)', fontSize: 'var(--font-size-sm)' }}>{imp.uploaded_by_name}</td>
                      <td style={{ padding: 'var(--space-2)', fontSize: 'var(--font-size-sm)', whiteSpace: 'nowrap' }}>
                        {imp.started_at
                          ? formatDateTime(imp.started_at)
                          : '\u2014'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            )}
          </Card>
          </>
          )}
        </>
      )}
    </div>
  );
}
